import { useRef } from 'react';
import { UploadCloud, X, FileText, CheckCircle2 } from 'lucide-react';

const GenericUploadZone = ({
  id,
  stepBadge = "STEP 1 • REQUIRED",
  title,
  subtitle,
  description,
  icon: Icon,
  colorClass = "teal",
  files = [],
  setFiles,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isDragging,
  isSubmitting,
  containerRef
}) => {
  const fileInputRef = useRef(null);

  const colors = {
    teal: {
      badgeBg: "bg-[#067978]/10 text-[#067978] border-[#067978]/20",
      iconBg: "bg-teal-50 text-[#067978] ring-1 ring-teal-100",
      accentText: "text-[#067978]",
      dragBg: "bg-teal-50/90 border-[#067978] ring-4 ring-[#067978]/10",
      dragBorder: "border-[#067978]",
      fileBg: "bg-teal-50/60 border-teal-100 text-teal-900",
      fileIcon: "text-[#067978]",
      dropBg: "hover:border-[#067978]/50 hover:bg-teal-50/20"
    }
  };

  const c = colors[colorClass] || colors.teal;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderFormattedText = (text) => {
    if (!text) return null;
    if (!text.includes('{highlight}')) return text;
    const parts = text.split('{highlight}');
    return (
      <>
        {parts[0]}
        <span className={`${c.accentText} font-bold`}>{parts[1]}</span>
        {parts[2]}
      </>
    );
  };

  return (
    <div
      id={id}
      ref={containerRef}
      className={`w-full transition-all duration-300 flex flex-col h-full ${
        isSubmitting ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <div className="bg-white/80 backdrop-blur-xl border border-slate-200/80 shadow-md rounded-[20px] sm:rounded-[24px] p-4 sm:p-6 flex flex-col justify-between h-full flex-1 relative group overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-slate-300">
        <div className="flex-1 flex flex-col justify-between space-y-3 sm:space-y-4">
          <div>
            {/* Header Row */}
            <div className="flex items-center justify-between gap-2.5 sm:gap-3 mb-3 sm:mb-4">
              <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider border ${c.badgeBg}`}>
                {(stepBadge.includes('REQUIRED') || stepBadge.includes('MANDATORY')) && <span className="w-1.5 h-1.5 rounded-full bg-[#067978] animate-pulse" />}
                {stepBadge}
              </span>

              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${c.iconBg}`}>
                <Icon size={18} className="sm:hidden" />
                <Icon size={20} className="hidden sm:block" />
              </div>
            </div>

            {/* Title & Description */}
            <div className="mb-3 sm:mb-4">
              <h3 className="text-base sm:text-xl font-bold text-slate-800 tracking-tight leading-snug mb-1">
                {renderFormattedText(subtitle || title)}
              </h3>
              <p className="text-slate-500 text-[11px] sm:text-xs font-medium leading-relaxed">
                {description}
              </p>
            </div>
          </div>

          {/* Drop Area Zone */}
          <div
            className={`
              relative flex-1 min-h-[120px] sm:min-h-[140px] border-2 border-dashed rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 group/drop
              ${
                isDragging
                  ? c.dragBg
                  : `border-slate-200 bg-slate-50/40 ${c.dropBg}`
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              multiple
              accept=".pdf,image/*,video/*"
              onChange={(e) => {
                const selectedFiles = Array.from(e.target.files || []);
                if (selectedFiles.length > 0) {
                  setFiles(prev => {
                    const existingNames = new Set(prev.map(f => f.name + f.size));
                    const newFiles = selectedFiles.filter(f => !existingNames.has(f.name + f.size));
                    return [...prev, ...newFiles];
                  });
                }
                e.target.value = '';
              }}
              className="hidden"
              ref={fileInputRef}
            />

            <div className="w-11 h-11 rounded-2xl bg-white shadow-xs border border-slate-100 flex items-center justify-center mb-2.5 transition-transform duration-300 group-hover/drop:scale-105">
              <UploadCloud size={22} className={c.accentText} />
            </div>

            <p className="text-xs font-semibold text-slate-700 group-hover/drop:text-slate-900 transition-colors">
              Click to browse <span className="text-slate-400 font-normal">or drop files</span>
            </p>
            <p className="text-[10px] font-medium text-slate-400 mt-0.5">
              Supports PDF, PNG, JPG, Screenshots
            </p>
          </div>
        </div>

        {/* Uploaded Files Drawer */}
        {files.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100 space-y-2 animate-in fade-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 px-1">
              <span className="flex items-center gap-1.5 text-slate-700 font-bold">
                <CheckCircle2 size={13} className="text-emerald-500" />
                Attached Files ({files.length})
              </span>
              <button
                type="button"
                onClick={() => setFiles([])}
                className="text-slate-400 hover:text-rose-600 transition-colors text-[10px] font-semibold cursor-pointer"
              >
                Clear all
              </button>
            </div>

            <div className="max-h-[130px] overflow-y-auto pr-1 space-y-1.5 custom-scrollbar">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className={`flex items-center justify-between p-2 rounded-xl border text-xs font-medium transition-all ${c.fileBg}`}
                >
                  <div className="flex items-center gap-2 min-w-0 pr-2">
                    <FileText size={14} className={`shrink-0 ${c.fileIcon}`} />
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-800 text-[11px] leading-tight" title={file.name}>
                        {file.name}
                      </p>
                      {file.size && (
                        <p className="text-[9px] font-medium text-slate-400">
                          {formatFileSize(file.size)}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFiles(prev => prev.filter((_, i) => i !== index));
                    }}
                    className="p-1 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0 cursor-pointer"
                    title="Remove file"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericUploadZone;
